<template>
  <v-card>
    <v-card-title>
      Élèves {{ course.student_courses ? course.student_courses.length : 0 }}
      <v-dialog v-if="isAdmin" v-model="dialog" width="900px">
        <template v-slot:activator="{ on }">
          <v-btn class="ml-4" v-on="on" color="primary" outlined @click.native="editItem">
            {{ descriptions.register_new_student }}
          </v-btn>
        </template>
        <v-form ref="form" v-model="valid" lazy-validation>
        <v-card>
          <v-card-title>
            <span class="text-h5">{{ descriptions.register }}</span>
          </v-card-title>
          <v-card-text>
            <v-row>
              <v-col cols="6">
                <v-autocomplete
                  label="Nom et prénom de l'élève"
                  :items="students"
                  v-model="student"
                  return-object
                  item-text="full_name"
                  item-value="id"
                />
              </v-col>
              <v-col cols="3">
                <v-select label="Niveau" :items="course.levels" v-model="level" />
              </v-col>
              <v-col cols="3">
                <v-menu
                  v-model="startDateMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      :value="formatDate(startDate)"
                      label="Date de début"
                      prepend-icon="mdi-calendar"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="startDate"
                    @input="startDateMenu = false"
                    :min="course.start"
                    :max="course.end"
                    :allowed-dates="functionEvents"
                    :events="functionEvents"
                    event-color="green"
                    locale="fr-CH"
                    first-day-of-week="1"
                  ></v-date-picker>
                </v-menu>
              </v-col>
            </v-row>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="blue darken-1" text @click.native="close()">Annuler</v-btn>
            <v-btn color="blue darken-1" text :disabled="isDisabled" @click.native="registerStudentToCourse()">
              Sauvegarder
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-form>
      </v-dialog>
    </v-card-title>
    <div class="mx-4 my-2 d-flex align-center flex-wrap">
      <v-dialog ref="dateDialog" v-model="dateDialog" persistent width="400">
        <template v-slot:activator="{ on }">
          <v-btn v-on="on" color="primary" class="mr-3">
            <v-icon left>mdi-calendar</v-icon>
            {{ date_list.length === 0 ? "Choisir date(s)" : "Modifier" }}
          </v-btn>
        </template>
        <v-card>
          <v-card-title class="text-subtitle-1">Sélectionner les dates</v-card-title>
          <v-divider></v-divider>
          <v-date-picker
            v-model="date_list"
            multiple
            no-title
            full-width
            :allowed-dates="functionEvents"
            locale="fr-CH"
            first-day-of-week="1"
            :events="functionEvents"
            event-color="green"
          ></v-date-picker>
          <v-divider></v-divider>
          <v-card-actions>
            <v-btn text color="grey" @click="date_list = []">Effacer</v-btn>
            <v-spacer></v-spacer>
            <v-btn text @click="dateDialog = false">Annuler</v-btn>
            <v-btn color="primary" text @click="dateDialog = false">OK</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-chip
        v-for="date in date_list"
        :key="date"
        close
        small
        color="primary"
        text-color="white"
        class="mr-1 mb-1"
        @click:close="removeDate(date)"
      >
        {{ formatDate(date) }}
      </v-chip>
      <span v-if="date_list.length === 0" class="grey--text text-body-2">
        Sélectionnez une date pour modifier les présences
      </span>
      <v-spacer></v-spacer>
      <v-text-field
        v-model="search"
        append-icon="mdi-magnify"
        label="Chercher"
        single-line
        hide-details
        dense
        style="max-width: 500px"
      ></v-text-field>
    </div>
    <!-- Loading state -->
    <div
      v-if="loadingExternal && (!course.student_courses || course.student_courses.length === 0)"
      class="pa-8 text-center"
    >
      <v-progress-circular indeterminate color="primary" size="48"></v-progress-circular>
      <div class="mt-4 grey--text">Chargement des élèves...</div>
    </div>
    <v-data-table
      v-else-if="course.student_courses && course.student_courses.length > 0"
      :headers="headers"
      :items="course.student_courses"
      :loading="loading || loadingExternal"
      :items-per-page="-1"
      :search="search"
      hide-default-footer
    >
      <template v-slot:body="{ items }">
        <tbody>
          <tr v-for="item in items" :key="item.id" :class="{ 'grey lighten-3 stopped-row': hasStopped(item), 'grey lighten-3 not-started-row': hasNotStarted(item) }">
            <td @click="$router.push(`/students/${item.student.id}`)" class="clickable">
              {{ item.student.last_name.toUpperCase() }} {{ item.student.first_name }}
            </td>
            <td>{{ item.level || UNDEFINED }}</td>
            <td>
              <v-select
                :key="'status-' + item.id + '-' + selectKey"
                :value="null"
                :items="combinedStatuses"
                item-text="text"
                item-value="value"
                :placeholder="getDisplayStatus(item)"
                @change="(val) => saveStatus(item, val)"
                :disabled="$v.date_list.$error || isEditDisabled(item)"
                return-object
                dense
                hide-details
                class="status-select"
              ></v-select>
            </td>
            <td :class="{ 'comment-disabled': isEditDisabled(item) || $v.date_list.$error || !getCommentStateType(item) }">
              <v-edit-dialog
                v-if="!isEditDisabled(item) && !$v.date_list.$error && getCommentStateType(item)"
                @save="saveComment(item)"
                large
              >
                <span :class="getActiveComment(item).trim() ? '' : 'grey--text'">
                  {{ getActiveComment(item) }}
                </span>
                <template v-slot:input>
                  <v-text-field
                    :value="getActiveComment(item)"
                    @input="setActiveComment(item, $event)"
                    label="Commentaire"
                    single-line
                    autofocus
                  ></v-text-field>
                </template>
              </v-edit-dialog>
              <span v-else class="grey--text">
                {{ getActiveComment(item) }}
              </span>
            </td>
            <td>{{ item.presenceRate }}%</td>
            <td class="text-no-wrap">
              <div v-if="hasCustomStartDate(item)" class="blue--text text-caption">
                Début: {{ formatDate(item.started_at) }}
              </div>
              <div
                v-if="item.stopped_at"
                class="orange--text text-caption clickable"
                @click="isAdmin && openStopDialog(item)"
              >
                Fin: {{ formatDate(item.stopped_at) }}
              </div>
              <div
                v-if="!item.stopped_at && isAdmin"
                class="grey--text text-caption clickable"
                @click="openStopDialog(item)"
              >
                + Terminer
              </div>
            </td>
            <td>
              <v-btn :to="`/students/${item.student.id}`" text icon color="purple">
                <v-icon>mdi-information-outline</v-icon>
              </v-btn>
            </td>
            <td>
              <v-btn v-if="isAdmin" @click="unsubscribe(item)" icon color="red">
                <v-icon>mdi-delete</v-icon>
              </v-btn>
            </td>
          </tr>
        </tbody>
      </template>
    </v-data-table>
    <!-- Stop enrollment dialog -->
    <v-dialog v-model="stopDialog" max-width="450px">
      <v-card>
        <v-card-title>Arrêt de participation</v-card-title>
        <v-card-text>
          <p v-if="stopItem">
            <strong>{{ stopItem.student?.last_name }} {{ stopItem.student?.first_name }}</strong>
          </p>
          <p class="caption grey--text">
            À partir de cette date, l'élève ne sera plus compté dans le taux de présence.
          </p>
          <v-menu
            v-model="stopDateMenu"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="auto"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                :value="formatDate(stopDate)"
                label="Ne participe plus à partir du"
                prepend-icon="mdi-calendar"
                readonly
                v-bind="attrs"
                v-on="on"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="stopDate"
              @input="stopDateMenu = false"
              :min="course.start"
              :max="course.end"
              :allowed-dates="functionEvents"
              :events="functionEvents"
              event-color="green"
              locale="fr-CH"
              first-day-of-week="1"
            ></v-date-picker>
          </v-menu>
        </v-card-text>
        <v-card-actions>
          <v-btn v-if="stopItem && stopItem.stopped_at" color="green" text @click="clearStopDate">Réactiver</v-btn>
          <v-spacer></v-spacer>
          <v-btn text @click="stopDialog = false">Annuler</v-btn>
          <v-btn color="orange" text @click="stopEnrollment" :disabled="!stopDate">Confirmer</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>
<script>
import { required, minLength } from "vuelidate/lib/validators"
import { mapGetters } from "vuex"
import Student from "@/models/Student"
import Presence from "@/models/Presence"
import Checkin from "@/models/Checkin"
import Course from "@/models/Course"
import StudentCourse from "@/models/StudentCourse"
import Description from "@/models/Description"

export default {
  props: ["course", "date", "loadingExternal"],
  data() {
    return {
      UNDEFINED: Description.UNDEFINED,
      snack: false,
      snackColor: "",
      snackText: "",
      loading: false,
      selectKey: 0,
      search: "",
      level: "",
      comment: "",
      student: "",
      dialog: false,
      dateDialog: false,
      valid: true,
      date_list: [],
      presence: {},
      students: [],
      // Registration start date
      startDate: null,
      startDateMenu: false,
      // Stop enrollment
      stopDialog: false,
      stopItem: null,
      stopDate: null,
      stopDateMenu: false,
      // Multi-date presence cache: { [date]: { [scId]: { presences, checkin } } }
      presenceCache: {},
    }
  },
  validations: {
    date_list: {
      required,
      minLength: minLength(1),
    },
  },
  computed: {
    ...mapGetters("Auth", ["isAdmin"]),
    combinedStatuses() {
      const presenceStatuses = Presence.statuts(false, this.isAdmin).map((s) => ({
        ...s,
        type: "presence",
      }))
      const checkinTypes = Checkin.types().map((t) => ({
        text: t.text,
        value: t.value,
        type: "checkin",
      }))
      return [
        { header: "Présences" },
        ...presenceStatuses,
        { divider: true },
        { header: "Retards / Départs" },
        ...checkinTypes,
      ]
    },
    headers() {
      const base = [
        { text: "Nom Prénom", value: "student.last_name", align: "left", width: "25%" },
        { text: "Niveau", value: "level", align: "left", width: "5%" },
        { text: "Présence", value: "presence", width: "25%" },
        { text: "Commentaire", value: "comment", width: "25%" },
        { text: "Taux", value: "presenceRate", width: "5%" },
        { text: "Statut", value: "status", sortable: false, width: "5%" },
        { text: "", value: "actions", sortable: false, width: "5%" },
      ]
      if (this.isAdmin) {
        base.push({ text: "Désinscrire", value: "unsubscribe", sortable: false, width: "5%" })
      }
      return base
    },
    isDisabled() {
      return !this.student
    },
    descriptions() {
      return Description.every()
    },
  },
  watch: {
    dialog(value) {
      value || this.close()
    },
    date(value) {
      if (value) {
        this.date_list = [value]
      }
    },
    date_list: {
      async handler(newVal, oldVal) {
        if (!this.course?.student_courses) return
        // Don't process while date dialog is open - wait until it closes
        if (this.dateDialog) return

        if (newVal.length === 1) {
          const date = newVal[0]
          if (date !== this.date) {
            this.$emit("dateChange", date)
          } else if (this.presenceCache[date]) {
            this.restorePresencesFromCache(date)
          } else {
            // No cache available, force parent to re-fetch
            this.$emit("update", this.course)
          }
        } else if (newVal.length > 1) {
          await this.fetchAndMergeMultipleDates()
        }
      },
      deep: true,
    },
    dateDialog(newVal) {
      if (!newVal && this.course?.student_courses) {
        // Dialog closed - process the selected dates
        if (this.date_list.length === 1) {
          const date = this.date_list[0]
          if (date !== this.date) {
            this.$emit("dateChange", date)
          } else if (this.presenceCache[date]) {
            this.restorePresencesFromCache(date)
          } else {
            this.$emit("update", this.course)
          }
        } else if (this.date_list.length > 1) {
          this.fetchAndMergeMultipleDates()
        }
      }
    },
    loadingExternal(newVal, oldVal) {
      if (oldVal && !newVal && this.course?.student_courses && this.date) {
        // Parent finished loading - cache presences for the fetched date
        this.cachePresencesFromCourse(this.date)
        // If in multi-date mode, re-merge after parent's fetch
        if (this.date_list.length > 1) {
          this.fetchAndMergeMultipleDates()
        }
      }
    },
    course() {
      this.level = this.course?.levels?.[0]
    },
  },
  async mounted() {
    // manually vuelidate field on mount
    this.$v.date_list.$touch()
    this.level = this.course?.levels?.[0]
  },
  methods: {
    openStopDialog(student_course) {
      this.stopItem = student_course
      this.stopDate = student_course.stopped_at || null
      this.stopDialog = true
    },
    async stopEnrollment() {
      if (!this.stopItem || !this.stopDate) return

      this.loading = true
      await StudentCourse.stopEnrollment(this.stopItem.id, this.stopDate)
      this.stopDialog = false
      this.stopItem = null
      this.loading = false
      this.$emit("update", this.course)
    },
    async clearStopDate() {
      if (!this.stopItem) return

      this.loading = true
      await StudentCourse.updateEnrollmentDates(this.stopItem.id, { stopped_at: null })
      this.stopDialog = false
      this.stopItem = null
      this.stopDate = null
      this.loading = false
      this.$emit("update", this.course)
    },
    hasStopped(item) {
      if (!item.stopped_at) return false
      const today = new Date().toISOString().split("T")[0]
      return item.stopped_at <= today
    },
    hasCustomStartDate(item) {
      if (!item.started_at) return false
      return item.started_at !== this.course?.start
    },
    isEditDisabled(item) {
      // Check if any selected date is before the student's started_at date
      if (item.started_at && item.started_at !== this.course?.start) {
        if (this.date_list.some((date) => date < item.started_at)) return true
      }
      // Check if any selected date is on or after the student's stopped_at date
      if (!item.stopped_at) return false
      return this.date_list.some((date) => date >= item.stopped_at)
    },
    hasNotStarted(item) {
      if (!item.started_at || item.started_at === this.course?.start) return false
      return this.date_list.length > 0 && this.date_list.every((date) => date < item.started_at)
    },
    formatDate(dateStr) {
      if (!dateStr) return ""
      const date = new Date(dateStr)
      return date.toLocaleDateString("fr-CH", { day: "2-digit", month: "2-digit", year: "numeric" })
    },
    removeDate(date) {
      this.date_list = this.date_list.filter((d) => d !== date)
    },
    getDisplayStatus(item) {
      if (item._distinctStatuses) {
        return item._distinctStatuses
      }
      const presenceStatus = item.presences?.statut?.trim()
      // If there's an absence, show the absence status
      if (presenceStatus && presenceStatus !== "présent") {
        return presenceStatus
      }
      // If student is present but has a checkin, show the checkin type
      if (item.checkin?.type) {
        return Checkin.TYPE_LABELS[item.checkin.type] || item.checkin.type
      }
      return presenceStatus || "Sélectionner"
    },
    async saveStatus(student_course, selectedStatus) {
      if (!selectedStatus || this.date_list.length === 0) return
      this.selectKey++

      if (this.date_list.length > 1) {
        if (!window.confirm(`Appliquer aux dates: ${this.date_list.join(", ")}?`)) {
          return
        }
      }

      this.loading = true
      const comment = this.getActiveComment(student_course)

      if (selectedStatus.type === "checkin") {
        // Save checkin for each date
        for (const date of this.date_list) {
          await Checkin.post({
            student_course_id: student_course.id,
            type: selectedStatus.value,
            date: date,
            comment: comment,
          })
        }
        // Also set presence to "présent" to clear any previous absence
        const presence = new Presence()
        presence.student_course_id = student_course.id
        presence.statut = "présent"
        presence.comment = comment
        await Presence.postMultiple(presence, this.date_list)
      } else {
        // Save as presence
        const presence = new Presence()
        presence.student_course_id = student_course.id
        presence.statut = selectedStatus.value
        presence.comment = comment
        await Presence.postMultiple(presence, this.date_list)
        // Clear any checkins for these dates (including when resetting to "présent")
        for (const date of this.date_list) {
          await Checkin.deleteByDateAndStudentCourse(student_course.id, date)
        }
      }
      this.invalidateCache()
      this.loading = false
      this.$emit("update", this.course)
    },
    getCommentStateType(item) {
      const presenceStatus = item.presences?.statut?.trim()
      if (presenceStatus && presenceStatus !== "présent") {
        return "presence"
      }
      if (item.checkin?.type) {
        return "checkin"
      }
      return null
    },
    getActiveComment(item) {
      const type = this.getCommentStateType(item)
      if (type === "checkin") {
        return item.checkin?.comment || ""
      }
      if (type === "presence") {
        return item.presences?.comment || ""
      }
      return ""
    },
    setActiveComment(item, value) {
      const type = this.getCommentStateType(item)
      if (type === "checkin") {
        this.$set(item.checkin, "comment", value)
      } else if (type === "presence") {
        this.$set(item.presences, "comment", value)
      }
    },
    async saveComment(item) {
      if (this.date_list.length === 0) return

      const type = this.getCommentStateType(item)
      if (!type) return

      this.loading = true
      if (type === "checkin") {
        for (const date of this.date_list) {
          await Checkin.post({
            student_course_id: item.id,
            type: item.checkin.type,
            date: date,
            comment: item.checkin.comment || "",
          })
        }
      } else {
        const presence = new Presence()
        presence.student_course_id = item.id
        presence.statut = item.presences?.statut?.trim() || "présent"
        presence.comment = item.presences?.comment || ""
        await Presence.postMultiple(presence, this.date_list)
      }
      this.invalidateCache()
      this.loading = false
    },
    close() {
      this.dialog = false
    },
    async editItem() {
      this.loading = true
      this.dialog = true
      // Set default start date to course start date
      this.startDate = this.course?.start || null
      let result = await Student.getNotSaving().finally(() => {
        this.loading = false
      })
      this.students = result.response.data.data
    },
    registerStudentToCourse() {
      if (!this.student || !this.course.id) {
        return
      }

      Course.registerStudentToCourse(this.student.id, this.course.id, this.level, this.startDate)
        .then(() => {
          this.$emit("update", this.course)
          this.$refs.form.reset()
          this.startDate = null
        })
        .finally(() => {
          this.close()
        })
    },
    async unsubscribe(student_course) {
      if (!confirm("Êtes-vous sûr de vouloir désinscrire cet élève de ce cours ?")) return

      this.loading = true
      await StudentCourse.unsubscribe(student_course.id)
      this.loading = false
      this.$emit("update", this.course)
    },
    cachePresencesFromCourse(date) {
      if (!this.course?.student_courses) return
      const dateData = {}
      this.course.student_courses.forEach((sc) => {
        dateData[sc.id] = {
          presences: { ...(sc.presences || { statut: "présent", comment: null }) },
          checkin: { ...(sc.checkin || { type: null, comment: null }) },
        }
      })
      this.$set(this.presenceCache, date, dateData)
    },
    async fetchPresencesForDate(date) {
      if (this.presenceCache[date]) return this.presenceCache[date]
      const response = await axios.get(
        `/api/v1/courses/with-students?id=${this.course.id}&date=${date}`
      )
      const studentCourses = response.data.data.student_courses || []
      const dateData = {}
      studentCourses.forEach((sc) => {
        dateData[sc.id] = {
          presences: sc.presences ? { ...sc.presences } : { statut: "présent", comment: null },
          checkin: sc.checkin ? { ...sc.checkin } : { type: null, comment: null },
        }
      })
      this.$set(this.presenceCache, date, dateData)
      return dateData
    },
    async fetchAndMergeMultipleDates() {
      this.loading = true
      try {
        await Promise.all(this.date_list.map((date) => this.fetchPresencesForDate(date)))
        this.applyMergedPresences()
      } finally {
        this.loading = false
      }
    },
    getStatusLabel(effectiveStatus) {
      if (!effectiveStatus) return "présent"
      if (effectiveStatus.startsWith("presence:")) return effectiveStatus.replace("presence:", "")
      if (effectiveStatus.startsWith("checkin:")) {
        const type = effectiveStatus.replace("checkin:", "")
        return Checkin.TYPE_LABELS[type] || type
      }
      return effectiveStatus
    },
    applyMergedPresences() {
      if (!this.course?.student_courses) return
      this.course.student_courses.forEach((sc) => {
        const allData = this.date_list.map((date) => this.presenceCache[date]?.[sc.id])
        if (allData.some((d) => !d)) {
          sc._distinctStatuses = null
          sc.presences = { statut: null, comment: null }
          sc.checkin = { type: null, comment: null }
          return
        }
        // Compute effective display status for each date
        const effectiveStatuses = allData.map((d) => {
          const presenceStatus = d.presences?.statut?.trim()
          if (presenceStatus && presenceStatus !== "présent") {
            return "presence:" + presenceStatus
          }
          if (d.checkin?.type) {
            return "checkin:" + d.checkin.type
          }
          return presenceStatus || null
        })
        const uniqueStatuses = new Set(effectiveStatuses)
        if (uniqueStatuses.size === 1) {
          sc._distinctStatuses = null
          const status = effectiveStatuses[0]
          if (status && status.startsWith("presence:")) {
            const comments = allData.map((d) => d.presences?.comment || "")
            sc.presences = {
              statut: status.replace("presence:", ""),
              comment: new Set(comments).size === 1 ? comments[0] : "",
            }
            sc.checkin = { type: null, comment: null }
          } else if (status && status.startsWith("checkin:")) {
            sc.presences = { statut: "présent", comment: null }
            const comments = allData.map((d) => d.checkin?.comment || "")
            sc.checkin = {
              type: status.replace("checkin:", ""),
              comment: new Set(comments).size === 1 ? comments[0] : "",
            }
          } else {
            sc.presences = { statut: status, comment: null }
            sc.checkin = { type: null, comment: null }
          }
        } else {
          // Different statuses - show distinct labels trimmed at 30 chars
          const distinctLabels = [...new Set(effectiveStatuses.map((s) => this.getStatusLabel(s)))]
          const labelText = distinctLabels.join(", ")
          sc._distinctStatuses = labelText.length > 50 ? labelText.substring(0, 50) + "…" : labelText
          sc.presences = { statut: null, comment: null }
          sc.checkin = { type: null, comment: null }
        }
      })
      this.selectKey++
    },
    restorePresencesFromCache(date) {
      const cached = this.presenceCache[date]
      if (!cached || !this.course?.student_courses) return
      this.course.student_courses.forEach((sc) => {
        sc._distinctStatuses = null
        const data = cached[sc.id]
        if (data) {
          sc.presences = { ...data.presences }
          sc.checkin = { ...data.checkin }
        }
      })
      this.selectKey++
    },
    invalidateCache() {
      this.presenceCache = {}
    },
    functionEvents(date) {
      return this.course.working_days.includes(date)
    },
  },
}
</script>

<style scoped>
.clickable {
  cursor: pointer;
}

.clickable:hover {
  background-color: rgba(0, 0, 0, 0.04);
}

.v-data-table >>> tbody tr:hover {
  background-color: #e3f2fd !important;
}

.v-data-table >>> tbody tr.stopped-row:hover,
.v-data-table >>> tbody tr.not-started-row:hover {
  background-color: #eeeeee !important;
}

.not-started-row td {
  opacity: 0.6;
}

.comment-disabled {
  background-color: rgba(0, 0, 0, 0.04);
}
</style>
